/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import {
  CdkCell,
  CdkCellDef,
  CdkColumnDef,
  CdkFooterCell,
  CdkFooterCellDef,
  CdkHeaderCell,
  CdkHeaderCellDef
} from '@angular/cdk/table';
import { Directive, ElementRef, Input } from '@angular/core';


/**
 * Cell definition for the mui-table.
 * Captures the template of a column's data row cell as well as cell-specific properties.
 */
@Directive({
  selector: '[muiCellDef]',
  providers: [{ provide: CdkCellDef, useExisting: MuiCellDef }]
})
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class MuiCellDef extends CdkCellDef {
}

/**
 * Header cell definition for the mui-table.
 * Captures the template of a column's header cell and as well as cell-specific properties.
 */
@Directive({
  selector: '[muiHeaderCellDef]',
  providers: [{ provide: CdkHeaderCellDef, useExisting: MuiHeaderCellDef }]
})
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class MuiHeaderCellDef extends CdkHeaderCellDef {
}

/**
 * Footer cell definition for the mui-table.
 * Captures the template of a column's footer cell and as well as cell-specific properties.
 */
@Directive({
  selector: '[muiFooterCellDef]',
  providers: [{ provide: CdkFooterCellDef, useExisting: MuiFooterCellDef }]
})
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class MuiFooterCellDef extends CdkFooterCellDef {
}

/**
 * Column definition for the mui-table.
 * Defines a set of cells available for a table column.
 */
@Directive({
  selector: '[muiColumnDef]',
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['sticky'],
  providers: [
    { provide: CdkColumnDef, useExisting: MuiColumnDef },
    { provide: 'MAT_SORT_HEADER_COLUMN_DEF', useExisting: MuiColumnDef }
  ]
})
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class MuiColumnDef extends CdkColumnDef {
  /** Unique name for this column. */
  @Input('muiColumnDef')
  get name(): string {
    return this._name;
  }

  set name(name: string) {
    this._setNameInput(name);
  }
}

/** Header cell template container that adds the right classes and role. */
@Directive({
  selector: 'th[muiHeaderCell]',
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    class: 'mui-table__header-cell',
    role: 'columnheader'
  }
})
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class MuiHeaderCell extends CdkHeaderCell {

  constructor(
    columnDef: CdkColumnDef,
    elementRef: ElementRef<HTMLElement>
  ) {
    super(columnDef, elementRef);
    elementRef.nativeElement.classList.add(`mui-column-${columnDef.cssClassFriendlyName}`);
  }
}

/** Footer cell template container that adds the right classes and role. */
@Directive({
  selector: 'td[muiFooterCell]',
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    class: 'mui-footer-cell',
    role: 'gridcell'
  }
})
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class MuiFooterCell extends CdkFooterCell {

  constructor(
    columnDef: CdkColumnDef,
    elementRef: ElementRef
  ) {
    super(columnDef, elementRef);
    elementRef.nativeElement.classList.add(`mui-column-${columnDef.cssClassFriendlyName}`);
  }
}

/** Cell template container that adds the right classes and role. */
@Directive({
  selector: 'td[muiCell]',
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    class: 'mui-table__cell',
    role: 'gridcell'
  }
})
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class MuiCell extends CdkCell {

  constructor(
    elementRef: ElementRef<HTMLElement>,
    columnDef: CdkColumnDef
  ) {
    super(columnDef, elementRef);
    elementRef.nativeElement.classList.add(`mui-column-${columnDef.cssClassFriendlyName}`);
  }
}
